import React from "react";
import styled, {css} from "styled-components";
import {useMessages} from "i18n";
import {EmailSuggestionChip} from "components/emailSuggestion/EmailSuggestionChip";
import {fontFamilies, radii} from "../../theme/tokens";

/**
 * v3 filled form primitives (#293): placeholder-only fields on the shared gray field surface — field
 * fill, control radius on the top corners, a `fieldLine` hairline underline that turns blue on focus,
 * matching {@link filledFieldSx} / BaseInput. Shared by the create card's sender-email field and the
 * options sheet's recipient chip row, so every input in the app reads as one filled family. Dumb,
 * token-driven views (epic #26).
 */

/** The filled field wrapper — the same surface as BaseInput's filled variant, minus the floating label. */
export const UnderlineField = styled.div`
	background: ${({theme}) => theme.tokens.field};
	border-top-left-radius: ${radii.control};
	border-top-right-radius: ${radii.control};
	border-bottom: 1px solid ${({theme}) => theme.tokens.fieldLine};
	padding: 0 12px;
	transition: border-color 0.18s;

	&:focus-within {
		border-bottom-color: ${({theme}) => theme.tokens.focusOutline};
	}
`;

const ufiCss = css`
	width: 100%;
	border: none;
	background: none;
	outline: none;
	font: 500 15px ${fontFamilies.sans};
	color: ${({theme}) => theme.tokens.ink};
	padding: 13px 2px;
	box-sizing: border-box;

	&::placeholder {
		color: ${({theme}) => theme.tokens.ink3};
	}
`;

const UnderlineInput = styled.input`
	${ufiCss};
`;

/** Sender-email row: the input plus the info-tooltip trigger (design `.email-uf`). */
const EmailRow = styled(UnderlineField)`
	display: flex;
	align-items: center;
	gap: 4px;

	input {
		flex: 1;
		min-width: 0;
		width: auto;
	}
`;

const InfoButton = styled.span`
	position: relative;
	flex: none;
	width: 26px;
	height: 26px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${({theme}) => theme.tokens.ink3};
	cursor: help;
	border-radius: 7px;
	transition: color 0.14s;
	outline: none;

	&:hover,
	&:focus-visible {
		color: ${({theme}) => theme.tokens.linkInk};
	}
`;

const Tip = styled.span`
	position: absolute;
	bottom: calc(100% + 9px);
	right: 4px;
	width: 206px;
	background: ${({theme}) => theme.tokens.ink};
	color: ${({theme}) => theme.tokens.paper};
	font-size: 11.5px;
	font-weight: 500;
	line-height: 1.45;
	padding: 9px 11px;
	border-radius: 10px;
	box-shadow: 0 10px 28px rgba(8, 14, 34, 0.3);
	opacity: 0;
	visibility: hidden;
	transform: translateY(5px);
	transition: opacity 0.16s, transform 0.16s, visibility 0.16s;
	pointer-events: none;
	z-index: 40;

	&::after {
		content: "";
		position: absolute;
		top: 100%;
		right: 12px;
		border: 6px solid transparent;
		border-top-color: ${({theme}) => theme.tokens.ink};
	}

	${InfoButton}:hover &,
	${InfoButton}:focus-visible & {
		opacity: 1;
		visibility: visible;
		transform: none;
	}
`;

interface EmailFieldProps {
	value: string;
	onChange: (value: string) => void;
	/** Tooltip copy shown on the info glyph. */
	tip: string;
	infoIcon: React.ReactNode;
	placeholder: string;
	id?: string;
	readOnly?: boolean;
}

/** Stacks the field over its "did you mean" suggestion, which is a sibling row rather than an adornment. */
const EmailStack = styled.div`
	display: flex;
	flex-direction: column;
`;

/**
 * Sender-email underline field with the "we'll email you the link" info tooltip (design `.email-uf`).
 *
 * The typo-suggestion chip (ui#1959) lives HERE rather than at the two call sites, so the signed-out
 * create card and any later consumer get it from one place. A `readOnly` field gets none: the signed-in
 * sender's identity row cannot be edited, so an offer to change it would be an offer to do nothing.
 */
export function EmailField({value, onChange, tip, infoIcon, placeholder, id, readOnly = false}: EmailFieldProps) {
	return (
		<EmailStack>
			<EmailRow>
				<UnderlineInput
					id={id}
					type="email"
					autoComplete="email"
					placeholder={placeholder}
					value={value}
					onChange={e => onChange(e.target.value)}
					aria-label={placeholder}
					readOnly={readOnly}
				/>
				<InfoButton tabIndex={0} role="note" aria-label={tip}>
					{infoIcon}
					<Tip>{tip}</Tip>
				</InfoButton>
			</EmailRow>
			{!readOnly && <EmailSuggestionChip value={value} onAccept={onChange}/>}
		</EmailStack>
	);
}

/** Recipient chips row + inline entry input (design `.chips-row` inside a `.uf`). */
export const ChipsRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	align-items: center;
	padding: 7px 0;
`;

/**
 * Compact recipient pill in the design's secondary identity (ui#213): a `signalTint` teal tint fill
 * with its accessible `signalInk` foreground. ~28px tall — its height follows the compact circular
 * remove control sized in {@link RecipientChipWrap}. Covers email AND group chips.
 */
const Chip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 2px;
	min-height: 28px;
	background: ${({theme}) => theme.tokens.signalTint};
	color: ${({theme}) => theme.tokens.signalInk};
	font-size: 13px;
	font-weight: 600;
	padding: 0 4px 0 12px;
	border-radius: ${radii.pill};

	button {
		border: none;
		background: none;
		color: ${({theme}) => theme.tokens.signalInk};
		cursor: pointer;
		font-size: 12px;
		line-height: 1;
		padding: 0;
		opacity: 0.72;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		transition: opacity 140ms ease, background 140ms ease;

		&:hover {
			opacity: 1;
			background: ${({theme}) => theme.tokens.signalTint};
		}
	}
`;

export const ChipInput = styled.input`
	flex: 1;
	min-width: 110px;
	border: none;
	background: none;
	outline: none;
	font: 500 14px ${fontFamilies.sans};
	color: ${({theme}) => theme.tokens.ink};
	height: 30px;
`;

/** A single recipient chip with its remove control (design `.rchip`). */
export function RecipientChip({email, onRemove}: {email: string; onRemove: () => void}) {
	const {sender} = useMessages();
	return (
		<Chip>
			{email}
			<button type="button" onClick={onRemove} aria-label={sender.recipients.removeAria(email)}>✕</button>
		</Chip>
	);
}
